import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { lazy, type ReactNode, Suspense } from 'react'
import toast from 'react-hot-toast'
import { ApiError, SERVER_ERROR_MESSAGE } from '@/gateway/errors'
import { reportError } from '@/utils/reportError'

/**
 * React Query owns ALL server state. Nothing fetched from the API is ever
 * copied into Zustand or Context — see State Management in AGENTS.md.
 */
/** One toast however many queries fail together — a dashboard of five is one message. */
const SERVER_ERROR_TOAST_ID = 'api-server-error'

export const queryClient = new QueryClient({
  /**
   * A 5xx on any **read** also raises a toast (client, 2026-10-01); the screen
   * still renders its inline error state. Writes are not toasted here: every
   * mutation call site already reports its own failure — which reads the same
   * "Something went wrong" — and a second toast would duplicate it.
   */
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof ApiError && error.kind === 'server') {
        toast.error(SERVER_ERROR_MESSAGE, { id: SERVER_ERROR_TOAST_ID })
      }
    },
  }),
  defaultOptions: {
    queries: {
      // Staff work fast and reload often; a short stale window keeps listings
      // snappy without hammering the API on every focus change.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) return error.isRetryable && failureCount < 2
        return failureCount < 2
      },
    },
    mutations: {
      retry: false,
      onError: (error) => reportError(error, { scope: 'mutation' }),
    },
  },
})

/** Devtools are dev-only and lazy — never part of a production bundle. */
const Devtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((module) => ({
    default: module.ReactQueryDevtools,
  })),
)

export function QueryProvider({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <Devtools buttonPosition="bottom-left" />
        </Suspense>
      )}
    </QueryClientProvider>
  )
}
